import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Usuario } from '../api';
import { listarUsuarios } from './api';

/**
 * El estado de servidor del ABM de usuarios: una sola lista, y una sola forma de
 * volver a pedirla.
 *
 * **No hay caché optimista.** Después de cada escritura se re-lee la lista
 * entera en vez de parchear la copia local con lo que devolvió la respuesta. El
 * listado es chico (son las personas de una empresa, no comprobantes) y a cambio
 * la pantalla nunca muestra un estado inventado por el cliente: si otro
 * administrador cambió algo en el medio, se ve en el mismo refresco. En una
 * pantalla cuyo trabajo es decir quién tiene permiso de administrar, mostrar
 * datos supuestos no vale el ahorro de un pedido.
 */

/** Clave de la lista. Una constante para que invalidar y consultar no se separen. */
export const CLAVE_USUARIOS = ['usuarios'] as const;

/** La lista completa: activos e inactivos. */
export function useUsuarios(): UseQueryResult<Usuario[], Error> {
  return useQuery({ queryKey: CLAVE_USUARIOS, queryFn: listarUsuarios });
}

/**
 * Vuelve a pedir lo que una escritura pudo haber cambiado.
 *
 * Además de la lista invalida `yo`, que es de dónde saca la barra superior el
 * nombre y el rol de quien está adentro: renombrarse a uno mismo tiene que verse
 * arriba en el acto y no dentro de un minuto, cuando venza el `staleTime`.
 */
export function useRefrescarUsuarios(): () => void {
  const cliente = useQueryClient();
  return useCallback(() => {
    void cliente.invalidateQueries({ queryKey: CLAVE_USUARIOS });
    void cliente.invalidateQueries({ queryKey: ['yo'] });
  }, [cliente]);
}
